import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  FormControl,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

type AuxiliaryTrendDialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  seriesOptions: any[];
  selectedSeriesKeys: string[];
  onSeriesChange: (value: string[]) => void;
  dateRange: { startDate: string; endDate: string };
  onDateRangeChange: (field: "startDate" | "endDate", value: string) => void;
  onApplyDateRange: () => void;
  onDownloadExcel: () => void;
  canDownload: boolean;
  isLoading: boolean;
  errorMessage: string;
};

const AuxiliaryTrendDialog = ({
  open,
  onClose,
  title,
  seriesOptions,
  selectedSeriesKeys,
  onSeriesChange,
  dateRange,
  onDateRangeChange,
  onApplyDateRange,
  onDownloadExcel,
  canDownload,
  isLoading,
  errorMessage,
}: AuxiliaryTrendDialogProps) => {
  const selectedSeries = useMemo(
    () =>
      seriesOptions.filter((series) =>
        (selectedSeriesKeys || []).includes(series.key)
      ),
    [seriesOptions, selectedSeriesKeys]
  );

  const displaySeries = useMemo(
    () => (selectedSeries.length > 0 ? selectedSeries : seriesOptions.slice(0, 1)),
    [selectedSeries, seriesOptions]
  );

  const colors = [
    "#1565c0",
    "#ef6c00",
    "#2e7d32",
    "#6a1b9a",
    "#00838f",
    "#ad1457",
    "#5d4037",
    "#4527a0",
  ];

  const chartData = useMemo(
    () => ({
      labels: displaySeries?.[0]?.labels || [],
      datasets: displaySeries.map((series, index) => ({
        label: series?.title || `Series ${index + 1}`,
        data: series?.values || [],
        borderColor: colors[index % colors.length],
        backgroundColor: `${colors[index % colors.length]}33`,
        pointRadius: 1,
        pointHoverRadius: 4,
        borderWidth: 2,
        tension: 0,
      })),
    }),
    [displaySeries]
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: "index" as const,
        intersect: false,
      },
      plugins: {
        legend: {
          display: true,
        },
        tooltip: {
          enabled: true,
          mode: "index" as const,
          intersect: false,
          callbacks: {
            title: (items: any[]) => {
              if (!items?.length) {
                return "";
              }
              return String(items[0]?.label || "");
            },
            label: (context: any) => {
              const value = context?.parsed?.y;
              const numericValue = Number(value);
              const series = displaySeries.find(
                (item: any) => item?.title === context?.dataset?.label
              );
              const unit = series?.unit ? ` ${series.unit}` : "";
              const formatted = Number.isFinite(numericValue)
                ? numericValue.toLocaleString(undefined, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 4,
                  })
                : String(value ?? "");
              return `${context?.dataset?.label || "Value"}: ${formatted}${unit}`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: {
            maxRotation: 0,
            minRotation: 0,
            maxTicksLimit: 8,
          },
          title: {
            display: true,
            text: "Time",
          },
        },
        y: {
          title: {
            display: true,
            text: "Value",
          },
        },
      },
    }),
    [displaySeries]
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flexWrap: "wrap",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
              Start Date
            </Typography>
            <TextField
              type="date"
              size="small"
              value={dateRange?.startDate || ""}
              onChange={(event) =>
                onDateRangeChange("startDate", event.target.value)
              }
              inputProps={{ "aria-label": "Start Date" }}
              sx={{ minWidth: 170 }}
            />
          </Box>
          <Box>
            <Typography variant="caption" sx={{ display: "block", mb: 0.5 }}>
              End Date
            </Typography>
            <TextField
              type="date"
              size="small"
              value={dateRange?.endDate || ""}
              onChange={(event) => onDateRangeChange("endDate", event.target.value)}
              inputProps={{ "aria-label": "End Date" }}
              sx={{ minWidth: 170 }}
            />
          </Box>
          <Button variant="outlined" onClick={onApplyDateRange} disabled={isLoading}>
            Apply Date Range
          </Button>
          <Button
            variant="contained"
            onClick={onDownloadExcel}
            disabled={isLoading || !canDownload}
          >
            Download Excel (CSV)
          </Button>
        </Box>

        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {!isLoading && errorMessage && <Alert severity="error">{errorMessage}</Alert>}

        {!isLoading && !errorMessage && seriesOptions.length === 0 && (
          <Typography variant="body2">No historical data found for this parameter.</Typography>
        )}

        {!isLoading && !errorMessage && seriesOptions.length > 0 && (
          <>
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth>
                <Select
                  multiple
                  value={selectedSeriesKeys || []}
                  sx={{
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 0.5,
                      minHeight: "42px",
                    },
                  }}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    onSeriesChange(
                      Array.isArray(nextValue) ? nextValue.map(String) : [String(nextValue)]
                    );
                  }}
                  renderValue={(selected) => {
                    const labels = (selected as string[])
                      .map(
                        (key) =>
                          seriesOptions.find((series) => series.key === key)?.title || key
                      )
                      .filter(Boolean);

                    return (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {labels.map((label) => (
                          <Chip
                            key={label}
                            label={label}
                            size="small"
                            sx={{
                              maxWidth: "100%",
                              "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              },
                            }}
                          />
                        ))}
                      </Box>
                    );
                  }}
                >
                  {seriesOptions.map((series) => (
                    <MenuItem value={series.key} key={series.key}>
                      {series.title}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Select one or more parameters to compare in the same chart.
                </FormHelperText>
              </FormControl>
            </Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {displaySeries.length > 1
                ? `Selected Parameters (${displaySeries.length})`
                : displaySeries?.[0]?.title || "Selected Parameter"}
            </Typography>
            <Box sx={{ height: 420 }}>
              <Line data={chartData} options={options} />
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuxiliaryTrendDialog;
